import { Flex, Icon, Stack, Table, Tbody, Text } from '@chakra-ui/react'
import { faBadgeCheck } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { DetailedUser, UserProperties } from 'BrokerTsLib/src/dtos'
import { getProductName, getUserTypeName, Product } from 'BrokerTsLib/src/enums'

import { OrganizationLink } from '@/components/OrganizationLink'
import { useAuth } from '@/context/AuthContext'
import { External, Group, Key, User, UserInfo } from '@/icons'
import { formatDateTime } from '@/utils/formatDate'

import { PpzAuthorizationSection } from '../../containers/user/PpzAuthorization'
import { TableSection } from '../Section/TableSection'
import { TableSectionRow } from '../Section/TableSectionRow'
import { UserLink } from '../UserLink'

export const PersonalInfo = ({
  user,
  properties,
}: {
  user?: DetailedUser
  properties?: UserProperties
}) => {
  const { user: loggedUser } = useAuth()

  return (
    <TableSection
      sectionTitle={
        <Flex justifyContent="space-between" alignItems="center">
          <Text>Informace</Text>
          {user?.dateCreated && !user?.dateModified && (
            <Text fontSize="sm" fontWeight="thin">
              Vytvořeno: {formatDateTime(user?.dateCreated)}{' '}
              {user.createdBy && (
                <UserLink userId={user.createdById} name={user.createdBy} />
              )}
            </Text>
          )}
          <Stack>
            {user?.dateModified && (
              <Text fontSize="sm" fontWeight="thin">
                Upraveno: {formatDateTime(user?.dateModified)}{' '}
                {user.modifiedBy && (
                  <UserLink userId={user.modifiedById} name={user.modifiedBy} />
                )}
              </Text>
            )}
            {user?.lastLogin && (
              <Text fontSize="sm" fontWeight="thin">
                Naposledy přihlášen: {formatDateTime(user?.lastLogin)}
              </Text>
            )}
          </Stack>
        </Flex>
      }
    >
      <Table variant={'simple'} fontSize={'md'} borderRadius={8}>
        <Tbody>
          <TableSectionRow icon={<Group />} title="Organizace">
            <OrganizationLink
              ouId={user?.organizationUnitId}
              name={user?.organizationUnitName}
            ></OrganizationLink>
          </TableSectionRow>
          <TableSectionRow icon={<UserInfo />} title="Typ uživatele">
            {getUserTypeName(user?.userTypeId)}
          </TableSectionRow>
          <TableSectionRow icon={<Key />} title="Role">
            {user?.roles[0]?.roleName}
          </TableSectionRow>
          <TableSectionRow icon={<User />} title="Username">
            {user?.userName}
          </TableSectionRow>
          {user?.userNameOtherSystem && (
            <TableSectionRow
              icon={<External />}
              title="Uživatelské jméno do jiných systémů"
            >
              {user?.userNameOtherSystem}
            </TableSectionRow>
          )}

          <TableSectionRow
            icon={<Icon as={FontAwesomeIcon} icon={faBadgeCheck} size="xl" />}
            title="Povolení pojišťování"
          >
            <Stack>
              <Text fontWeight={'semibold'}>
                {user?.insuringAllowed ? 'Povoleno' : 'Zakázáno'}
              </Text>
              {loggedUser?.vehicleRights && (properties?.vehicleInsuranceAllowed ?? true) && (
                <Text>{getProductName(Product.VehicleInsurance)}</Text>
              )}
              {loggedUser?.propertyRights && (properties?.propertyInsuranceAllowed ?? true) && (
                <Text>{getProductName(Product.PropertyInsurance)}</Text>
              )}
              {loggedUser?.travelRights && (properties?.travelInsuranceAllowed ?? true) && (
                <Text>{getProductName(Product.TravelInsurance)}</Text>
              )}
            </Stack>
          </TableSectionRow>

          {user && loggedUser?.enableAutorizacePpz && (
            <PpzAuthorizationSection userId={user.userId} />
          )}

          {/** Properties možná ve zvláštní sekci a přepínat komponentu podle partnera? */}
          {properties?.formaOdmeny && (
            <TableSectionRow icon title="Forma odměny">
              {properties.formaOdmeny}
            </TableSectionRow>
          )}
          {properties?.importInsurance && (
            <TableSectionRow icon title="PoLeas povolený">
              {properties.importInsurance ? 'Ano' : 'Ne'}
            </TableSectionRow>
          )}
        </Tbody>
      </Table>
    </TableSection>
  )
}
